import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Divider, Link, Stack} from '@mui/material';
import { SearchDocument } from '../../../types/search/searchDocument';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface Props {
    documents: SearchDocument[];
    searchInput: string;
}


const DocumentList = (props:Props): JSX.Element => {

    const [documentList, setDocumentList] = useState<SearchDocument[]>([]);
    const [toggleViewMore, setToggleViewMore] = useState(false);

    useEffect(() => {
      setDocumentList(props.documents.slice(0,3));
      setToggleViewMore(props.documents.length > 3? true: false);
    }, [props.documents]);

    const handleViewMore = () => {
      setToggleViewMore(!toggleViewMore);
      setDocumentList(props.documents);
   }

    const displayDocumentTypeIcon = (documentTypeId: number) => {

        switch(documentTypeId){
            case 100:
                return <HelpOutlineIcon color="primary" />;
            case 65:
                return <YouTubeIcon color="primary" />;
            default:
                return <MenuBookIcon color="primary" />;
        }
    }

    const getEmbededVideoUrl = (url: string)=> {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11)? match[2]: null;
    }

    const copyDocumentLink = (documentId: number) => {
      let url = `https://www.ocpf.us/PublicSearch/ViewDocument?id=${documentId}`;
      navigator.clipboard.writeText(url);
      alert('Link copied and the document will be opened in the new tab.');
    }

    return (
     <Box>
        <Typography
            variant="h6"
            align={'left'}
            sx={{
            fontWeight: 700,
            }}
            color={'secondary'}
            paddingTop={3}
            gutterBottom
        >
        General Information
        </Typography>
        <Divider textAlign="right">
            <Typography component={'i'} variant='body2' color="primary">{props.documents.length} total result(s)</Typography>
        </Divider>
        <Grid container marginTop={2}>
          <Grid size={{xs:12, md:10}}>
            {documentList.map((document) => (
            <Box
            component={Accordion}
            key={document.id}
            paddingY={1}
            elevation={0}
            sx={{
              '&:first-of-type': {
                borderTopLeftRadius: 1,
                borderTopRightRadius: 1,
              },
              '&:not(:first-of-type):before': {
                opacity: '1 !important',
                display: 'block !important',
              },
              '&.Mui-expanded': {
                margin: 0,
              },
            }}
          >
            <Box
              component={AccordionSummary}
              expandIcon={<ExpandMoreIcon />}
              padding={'0 !important'}
            >
              <Box>
                <Stack direction={'row'} spacing={1}>
                    {displayDocumentTypeIcon(document.documentTypeId)}
                    <Typography fontWeight={550} variant='subtitle1'>{document.title}</Typography>
                </Stack>
              </Box>
            </Box>
            <AccordionDetails>
                <Box
                    width={1}
                    height={1}
                    padding={2}
                    component={Card}
                    boxShadow={0}
                    variant={'outlined'}
                >
                    <Box marginBottom={1} display={'flex'} justifyContent={'right'} marginRight={3}>
                      <Button 
                        component={Link} 
                        size="small" 
                        startIcon={<ContentCopyIcon />} 
                        href={`/PublicSearch/ViewDocument?id=${document.id}`} 
                        underline='none'
                        target="_blank" 
                        onClick={() => copyDocumentLink(document.id)}>
                          Copy Link
                      </Button>
                    </Box>
                    <Typography color="text.secondary" component={"i"}><div dangerouslySetInnerHTML={{ __html: document.description }} /></Typography>
                    <div dangerouslySetInnerHTML={{ __html: document.content.replace(new RegExp(`(${props.searchInput})(?![^<]*>)`, 'gi'), (match:string) => `<mark>${match}</mark>`) }} />
                    {
                        document.documentTypeId === 65 &&
                        <Box 
                          marginTop={2}
                          height={{xs: 300, md: 400}}
                          display={"flex"}
                          justifyContent={"center"}
                          >
                            <iframe 
                              height={"100%"}
                              width={"80%"}
                              src={`https://www.youtube.com/embed/${getEmbededVideoUrl(document.url)}`} 
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share">
                            </iframe>
                        </Box>
                    }
                </Box>
            </AccordionDetails>
            </Box>
            ))}
          </Grid>
          {
            toggleViewMore && 
            <Grid size={10} display="flex" justifyContent="flex-end" marginTop={1}>
              <Link className='is-clickable' onClick={handleViewMore} underline='hover' fontSize={"small"} ><i>View More Results</i></Link>
            </Grid>
          }
        </Grid>
  </Box>
  );
};

export default DocumentList;